import {
    ArrowPathIcon,
    BuildingStorefrontIcon,
    CalendarIcon,
    CheckCircleIcon,
    EnvelopeIcon,
    EyeIcon,
    MagnifyingGlassIcon,
    MapPinIcon,
    NoSymbolIcon,
    PhoneIcon,
    TrashIcon,
    UserGroupIcon,
    UserIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import PhoneActions from '../../components/PhoneActions';

const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-green-100 text-green-800 border-green-200',
    inactive: 'bg-gray-100 text-gray-800 border-gray-200',
    blocked: 'bg-red-100 text-red-800 border-red-200',
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-200'
  };

  const displayStatus = status || 'active';

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${styles[displayStatus]}`}>
      {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
    </span>
  );
};

// Color-coded badges: Individual=Green, Agent-Created=Blue
const TypeBadge = ({ type, agentName }) => {
  const isAgentCreated = type === 'agent-created';
  const isIndividual = type === 'individual';

  return (
    <div className="flex items-center gap-2">
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
        isIndividual ? 'bg-green-100 text-green-800 border border-green-200' :
        isAgentCreated ? 'bg-blue-100 text-blue-800 border border-blue-200' :
        'bg-gray-100 text-gray-800 border border-gray-200'
      }`}>
        {isIndividual && <UserIcon className="w-3 h-3 mr-1" />}
        {isAgentCreated && <UserGroupIcon className="w-3 h-3 mr-1" />}
        {isIndividual ? 'Individual' : isAgentCreated ? 'Agent-Created' : 'Dealer'}
      </span>
      {agentName && (
        <span className="text-xs text-blue-600 font-medium">by {agentName}</span>
      )}
    </div>
  );
};

// Seller Detail Modal
const SellerDetailModal = ({ seller, isOpen, onClose }) => {
  if (!isOpen || !seller) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Background overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 transition-opacity bg-gray-500 bg-opacity-75"
          />

          {/* Modal panel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative z-50 inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
          >
            {/* Header */}
            <div className={`px-6 py-4 ${
              seller.sellerType === 'individual' 
                ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                : seller.sellerType === 'agent-created'
                ? 'bg-gradient-to-r from-blue-500 to-indigo-500'
                : 'bg-gradient-to-r from-gray-500 to-gray-600'
            }`}>
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-white">Seller Details</h3>
                <button
                  onClick={onClose}
                  className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
              {/* Profile Section */}
              <div className="flex items-start gap-6 mb-6 pb-6 border-b border-gray-200">
                <div className={`w-24 h-24 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  seller.sellerType === 'individual' 
                    ? 'bg-gradient-to-br from-green-500 to-emerald-500' 
                    : seller.sellerType === 'agent-created'
                    ? 'bg-gradient-to-br from-blue-500 to-indigo-500'
                    : 'bg-gradient-to-br from-gray-500 to-gray-600'
                }`}>
                  <span className="text-4xl font-bold text-white">
                    {(seller.firstName || seller.fullName || 'S').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-gray-900">
                    {seller.firstName && seller.lastName ? `${seller.firstName} ${seller.lastName}` : seller.fullName || 'N/A'}
                  </h4>
                  {seller.businessName && (
                    <p className="text-lg text-gray-600 mt-1">{seller.businessName}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2">
                    <StatusBadge status={seller.isActive ? 'active' : 'inactive'} />
                    <TypeBadge 
                      type={seller.sellerType} 
                      agentName={seller.agentId?.firstName ? `${seller.agentId.firstName} ${seller.agentId.lastName}` : null}
                    />
                  </div>
                  <div className="mt-3 text-sm text-gray-600">
                    Joined {seller.createdAt ? format(new Date(seller.createdAt), 'MMM dd, yyyy') : 'N/A'}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-6">
                <h5 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="text-xs text-gray-500">Email</div>
                      <div className="font-medium text-gray-900">{seller.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <PhoneIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="text-xs text-gray-500">Phone</div>
                      <PhoneActions phone={seller.phone} size="md" />
                    </div>
                  </div>
                  {seller.address && (seller.address.city || seller.address.state) && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg md:col-span-2">
                      <MapPinIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-xs text-gray-500">Address</div>
                        <div className="font-medium text-gray-900">
                          {[seller.address.street, seller.address.city, seller.address.state, seller.address.pincode]
                            .filter(Boolean).join(', ')}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Statistics */}
              <div className="mb-6">
                <h5 className="text-lg font-semibold text-gray-900 mb-4">Listing Statistics</h5>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {seller.stats?.totalCars || 0}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Total Cars</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl border border-green-100 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {seller.stats?.activeCars || 0}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Active Listings</div>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-xl border border-orange-100 text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {seller.stats?.soldCars || 0}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Sold Cars</div>
                  </div>
                </div>
              </div>

              {/* Agent Information (if agent-created) */}
              {seller.agentId && (
                <div className="mb-6">
                  <h5 className="text-lg font-semibold text-gray-900 mb-4">Associated Agent</h5>
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">
                          {seller.agentId.firstName?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">
                          {seller.agentId.firstName} {seller.agentId.lastName}
                        </div>
                        <div className="text-sm text-gray-600">{seller.agentId.email}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Membership Details */}
              {seller.membership && (
                <div className="mb-6">
                  <h5 className="text-lg font-semibold text-gray-900 mb-4">Membership Details</h5>
                  <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Type</div>
                        <div className="font-semibold text-gray-900">
                          {(seller.membership.type || 'basic').charAt(0).toUpperCase() + (seller.membership.type || 'basic').slice(1)}
                        </div>
                      </div>
                      {seller.membership.startDate && (
                        <div>
                          <div className="text-xs text-gray-600 mb-1">Start Date</div>
                          <div className="font-semibold text-gray-900">
                            {format(new Date(seller.membership.startDate), 'MMM dd, yyyy')}
                          </div>
                        </div>
                      )}
                      {seller.membership.endDate && (
                        <div>
                          <div className="text-xs text-gray-600 mb-1">End Date</div>
                          <div className="font-semibold text-gray-900">
                            {format(new Date(seller.membership.endDate), 'MMM dd, yyyy')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Info */}
              <div>
                <h5 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Seller ID</div>
                    <div className="font-medium text-gray-900 font-mono text-sm">{seller._id || seller.id}</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Account Status</div>
                    <div className="font-medium text-gray-900">
                      {seller.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  {seller.gstNumber && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500 mb-1">GST Number</div>
                      <div className="font-medium text-gray-900">{seller.gstNumber}</div>
                    </div>
                  )}
                  {seller.rating && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500 mb-1">Rating</div>
                      <div className="font-medium text-gray-900">
                        {typeof seller.rating === 'object' 
                          ? `${seller.rating.average?.toFixed(1) || 0}/5 (${seller.rating.count || 0} reviews)`
                          : `${seller.rating}/5`
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};


// Where a seller or agent is based. Accounts keep it in `address`; a few
// older records used `location`.
const placeOf = (u) => {
  const a = (u && u.address) || {};
  const l = (u && typeof u.location === 'object' && u.location) || {};
  return {
    city: String(a.city || l.city || '').trim(),
    state: String(a.state || l.state || '').trim(),
  };
};

const NOT_SET = '__not_set__';

/** "Andhra Pradesh 12 · Telangana 4 · Not set 3" - click one to filter. */
const StateSummary = ({ people, value, onChange, noun }) => {
  const counts = {};
  let unset = 0;
  people.forEach((p) => {
    const { state } = placeOf(p);
    if (state) counts[state] = (counts[state] || 0) + 1;
    else unset += 1;
  });
  const states = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  if (!states.length && !unset) return null;
  const pill = (key, label, n, warn) => (
    <button
      key={key}
      type="button"
      onClick={() => onChange(value === key ? 'all' : key)}
      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
        value === key
          ? 'bg-primary-600 text-white border-primary-600'
          : warn
            ? 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
      }`}
    >
      {label} <span className="font-semibold">{n}</span>
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-gray-500 mr-1">{noun} by state:</span>
      {states.map((s) => pill(s, s, counts[s]))}
      {unset ? pill(NOT_SET, 'Location not set', unset, true) : null}
    </div>
  );
};

const SellerCard = ({ seller, onAction, onViewDetails }) => {
  const stats = seller.stats || {};
  const sellerType = seller.sellerType || 'individual';
  const agentName = seller.agentId ? `${seller.agentId.firstName} ${seller.agentId.lastName}` : null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className={`bg-white rounded-xl p-6 shadow-sm border-2 hover:shadow-lg transition-all duration-300 ${
        sellerType === 'individual' 
          ? 'border-green-200 hover:border-green-400' 
          : sellerType === 'agent-created'
          ? 'border-blue-200 hover:border-blue-400'
          : 'border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
              sellerType === 'individual' 
                ? 'bg-gradient-to-br from-green-500 to-emerald-500' 
                : sellerType === 'agent-created'
                ? 'bg-gradient-to-br from-blue-500 to-indigo-500'
                : 'bg-gradient-to-br from-gray-500 to-gray-600'
            }`}>
              <span className="text-2xl font-bold text-white">
                {(seller.firstName || seller.fullName || 'S').charAt(0).toUpperCase()}
              </span>
            </div>
            <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white ${
              seller.isActive ? 'bg-green-500' : 'bg-gray-400'
            }`}></div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {seller.firstName && seller.lastName ? `${seller.firstName} ${seller.lastName}` : seller.fullName || 'N/A'}
            </h3>
            {seller.businessName && (
              <p className="text-sm text-gray-600">{seller.businessName}</p>
            )}
            <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
              <PhoneActions phone={seller.phone} textClassName="text-gray-600 font-normal" />
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
              <CalendarIcon className="w-4 h-4" />
              <span>Joined {seller.createdAt ? format(new Date(seller.createdAt), 'MMM dd, yyyy') : 'N/A'}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end space-y-2">
          <StatusBadge status={seller.isActive ? 'active' : 'inactive'} />
          <TypeBadge type={sellerType} agentName={agentName} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
          <div className="text-xl font-bold text-blue-600">{stats.totalCars || 0}</div>
          <div className="text-xs text-gray-600">Total Cars</div>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
          <div className="text-xl font-bold text-green-600">{stats.activeCars || 0}</div>
          <div className="text-xs text-gray-600">Active</div>
        </div>
        <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-100">
          <div className="text-xl font-bold text-orange-600">{stats.soldCars || 0}</div>
          <div className="text-xs text-gray-600">Sold</div>
        </div>
      </div>

      <div className="space-y-2 mb-4 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Email:</span>
          <span className="text-gray-900 font-medium truncate max-w-[200px]">{seller.email}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Location:</span>
          {placeOf(seller).state ? (
            <span className="text-gray-900 font-medium">
              {[placeOf(seller).city, placeOf(seller).state].filter(Boolean).join(', ')}
            </span>
          ) : (
            <span className="text-amber-700 font-medium">Location not set</span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-200 gap-2">
        <button
          onClick={() => onViewDetails(seller)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors text-sm font-medium"
        >
          <EyeIcon className="w-4 h-4" />
          View
        </button>
        
        {seller.isActive ? (
          <button
            onClick={() => onAction('block', seller)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
          >
            <NoSymbolIcon className="w-4 h-4" />
            Block
          </button>
        ) : (
          <button
            onClick={() => onAction('unblock', seller)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors text-sm font-medium"
          >
            <CheckCircleIcon className="w-4 h-4" />
            Unblock
          </button>
        )}
        
        <button
          onClick={() => onAction('delete', seller)}
          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

const SellersManagement = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sellerTypeFilter, setSellerTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/sellers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSellers(res.data.data || res.data);
    } catch (error) {
      console.error('Failed to fetch sellers:', error);
      toast.error('Failed to load sellers');
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedSellers = useMemo(() => {
    const normalizedSearch = String(searchTerm || '').trim().toLowerCase();

    return sellers
      .filter(seller => {
        const searchableFields = [
          seller.firstName,
          seller.lastName,
          seller.fullName,
          seller.name,
          seller.email,
          seller.businessName,
          seller.phone,
          seller._id,
          seller.id,
          seller.address?.city,
          seller.address?.state,
        ]
          .filter(Boolean)
          .map((value) => String(value).toLowerCase());

        const matchesSearch = !normalizedSearch || searchableFields.some((value) => value.includes(normalizedSearch));
        const matchesType = sellerTypeFilter === 'all' || seller.sellerType === sellerTypeFilter;
        const matchesStatus = statusFilter === 'all' || 
                            (statusFilter === 'active' && seller.isActive) ||
                            (statusFilter === 'inactive' && !seller.isActive);
        const sellerState = placeOf(seller).state;
        const matchesState = stateFilter === 'all'
          || (stateFilter === NOT_SET ? !sellerState : sellerState === stateFilter);
        return matchesSearch && matchesType && matchesStatus && matchesState;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name':
            return (a.firstName || '').localeCompare(b.firstName || '');
          case 'cars':
            return (b.stats?.totalCars || 0) - (a.stats?.totalCars || 0);
          case 'joinDate':
            return new Date(b.createdAt) - new Date(a.createdAt);
          default:
            return 0;
        }
      });
  }, [sellers, searchTerm, sellerTypeFilter, statusFilter, stateFilter, sortBy]);

  const handleAction = async (action, seller) => {
    const token = localStorage.getItem('adminToken');
    
    try {
      switch (action) {
        case 'block':
          if (window.confirm(`Are you sure you want to block ${seller.firstName} ${seller.lastName}?`)) {
            await axios.patch(
              `${process.env.REACT_APP_API_URL}/api/admin/sellers/${seller._id}/block`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Seller blocked successfully');
            fetchSellers();
          }
          break;
          
        case 'unblock':
          await axios.patch(
            `${process.env.REACT_APP_API_URL}/api/admin/sellers/${seller._id}/unblock`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success('Seller unblocked successfully');
          fetchSellers();
          break;
          
        case 'delete':
          if (window.confirm(`Are you sure you want to permanently delete ${seller.firstName} ${seller.lastName}? This action cannot be undone.`)) {
            await axios.delete(
              `${process.env.REACT_APP_API_URL}/api/admin/sellers/${seller._id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Seller deleted successfully');
            fetchSellers();
          }
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.error(`Action ${action} failed:`, error);
      toast.error(error.response?.data?.message || `Failed to ${action} seller`);
    }
  };

  const handleViewDetails = (seller) => {
    setSelectedSeller(seller);
    setShowDetailModal(true);
  };

  const stats = {
    total: sellers.length,
    individual: sellers.filter(s => s.sellerType === 'individual').length,
    agentCreated: sellers.filter(s => s.sellerType === 'agent-created').length,
    dealer: sellers.filter(s => s.sellerType === 'dealer').length,
    active: sellers.filter(s => s.isActive).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <h1 className="text-3xl font-display font-bold text-gray-900">
            Sellers Management
          </h1>
          <p className="text-gray-600 mt-2">
            Manage individual and agent-created sellers with color-coded distinction
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center space-x-4 mt-4 lg:mt-0"
        >
          <button
            onClick={fetchSellers}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </motion.div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Sellers</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
            </div>
            <div className="p-4 bg-primary-100 rounded-xl">
              <UserIcon className="w-8 h-8 text-primary-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Individual</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{stats.individual}</p>
            </div>
            <div className="p-4 bg-green-100 rounded-xl">
              <UserIcon className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Agent-Created</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">{stats.agentCreated}</p>
            </div>
            <div className="p-4 bg-blue-100 rounded-xl">
              <UserGroupIcon className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Dealers</p>
              <p className="text-3xl font-bold text-gray-600 mt-2">{stats.dealer}</p>
            </div>
            <div className="p-4 bg-gray-100 rounded-xl">
              <BuildingStorefrontIcon className="w-8 h-8 text-gray-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{stats.active}</p>
            </div>
            <div className="p-4 bg-green-100 rounded-xl">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search sellers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-64"
              />
            </div>
            
            <select
              value={sellerTypeFilter}
              onChange={(e) => setSellerTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Types</option>
              <option value="individual">Individual</option>
              <option value="agent-created">Agent-Created</option>
              <option value="dealer">Dealer</option>
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-4">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="name">Sort by Name</option>
              <option value="cars">Sort by Cars Listed</option>
              <option value="joinDate">Sort by Join Date</option>
            </select>
            <span className="text-sm text-gray-500 whitespace-nowrap">
              {filteredAndSortedSellers.length} of {sellers.length} sellers
            </span>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100">
          <StateSummary people={sellers} value={stateFilter} onChange={setStateFilter} noun="Sellers" />
        </div>
      </motion.div>

      {/* Sellers Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      ) : filteredAndSortedSellers.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12 bg-white rounded-xl border border-gray-200"
        >
          <UserIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No sellers found</h3>
          <p className="text-gray-500">Try adjusting your search or filter criteria</p>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredAndSortedSellers.map((seller, index) => (
            <motion.div
              key={seller._id || seller.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * index }}
            >
              <SellerCard 
                seller={seller} 
                onAction={handleAction} 
                onViewDetails={handleViewDetails}
              />
            </motion.div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <SellerDetailModal
        seller={selectedSeller}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedSeller(null);
        }}
      />
    </div>
  );
};

export default SellersManagement;
